import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 4;

export default function NewsPage() {
  useDocumentTitle(
    "动漫资讯 - 漫蛙2动漫资讯平台",
    "漫蛙2动漫资讯频道，提供新番前瞻、深度解读、幕后科普与行业观察等动漫资讯内容。"
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "全部";
  const sort = searchParams.get("sort") || "newest";
  const [page, setPage] = useState(1);

  const categories = useMemo(() => ["全部", ...Array.from(new Set(newsList.map((n) => n.category)))], []);

  const filtered = useMemo(() => {
    const list = newsList.filter((n) => activeCategory === "全部" || n.category === activeCategory);
    const sorted = [...list].sort((a, b) => (sort === "oldest" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)));
    return sorted;
  }, [activeCategory, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "category" && value === "全部") next.delete("category");
    else next.set(key, value);
    if (key === "category") next.delete("page");
    setSearchParams(next);
    setPage(1);
  };

  const handleSort = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "newest") next.delete("sort");
    else next.set("sort", value);
    setSearchParams(next);
    setPage(1);
  };

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "动漫资讯" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">动漫资讯</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            新番前瞻、深度解读、幕后科普与行业观察。按分类筛选或调整排序，快速找到你感兴趣的动漫内容。
          </p>
        </div>
      </section>

      <div className="page-wrap mt-8">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => updateParam("category", category)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:text-primary-600"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-xs text-foreground-500">排序</span>
            <div className="flex rounded-full bg-background-100 p-1">
              {[
                { key: "newest", label: "最新发布" },
                { key: "oldest", label: "最早发布" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleSort(option.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    sort === option.key ? "bg-background-50 text-primary-700" : "text-foreground-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {paged.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title="暂无相关资讯"
              description="换个分类或稍后再来看看吧，也可以先浏览全部资讯。"
              actionLabel="查看全部资讯"
              actionTo="/news"
            />
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {paged.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        )}

        <Pagination current={current} total={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}